import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  requestNotificationPermission, 
  isNotificationGranted, 
  isNotificationSupported,
  hasActiveSubscription,
  unsubscribeFromNotifications,
  fetchNotifications,
  markAllAsRead
} from '../utils/NotificationManager';
import { playClick, playSuccess } from '../utils/SoundManager';
import { Bell, BellOff, MessageCircle, Heart, Check, X, User, Settings } from 'lucide-react';

// مكون شريط تفعيل الإشعارات
export function NotificationPermissionBanner({ onClose }) {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkPermission = async () => {
      const supported = isNotificationSupported();
      const granted = isNotificationGranted();
      const hasSubscription = await hasActiveSubscription();
      const asked = localStorage.getItem('notificationAsked');
      
      // عرض الشريط إذا كان مدعوماً ولم يتم السؤال بعد ولا يوجد اشتراك
      if (supported && !asked && (!granted || !hasSubscription)) {
        setTimeout(() => setShow(true), 2000);
      }
    };
    
    checkPermission();
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    playClick();
    
    const success = await requestNotificationPermission();
    
    if (success) {
      playSuccess();
    }
    
    localStorage.setItem('notificationAsked', 'true');
    setLoading(false);
    setShow(false);
    if (onClose) onClose();
  };

  const handleDismiss = () => {
    localStorage.setItem('notificationAsked', 'true');
    setShow(false);
    if (onClose) onClose();
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      left: '20px',
      right: '20px',
      background: 'linear-gradient(135deg, #667eea, #764ba2)',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 10px 40px rgba(102, 126, 234, 0.4)',
      zIndex: 1000,
      animation: 'slideInUp 0.5s ease'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div style={{ 
          background: 'rgba(255,255,255,0.2)', 
          borderRadius: '12px', 
          padding: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Bell size={32} color="white" />
        </div>
        <div style={{ flex: 1 }}>
          <h4 style={{ color: 'white', margin: '0 0 6px 0', fontSize: '16px', fontWeight: '700' }}>
            تفعيل الإشعارات الفورية
          </h4>
          <p style={{ color: 'rgba(255,255,255,0.85)', margin: 0, fontSize: '13px', lineHeight: 1.5 }}>
            احصل على إشعارات فورية عند وصول رسائل جديدة أو طلبات تواصل
          </p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
        <button
          onClick={handleEnable}
          disabled={loading}
          style={{
            flex: 1,
            background: 'white',
            color: '#667eea',
            border: 'none',
            borderRadius: '10px',
            padding: '12px',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer',
            opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? '⏳ جاري التفعيل...' : '✅ تفعيل الإشعارات'}
        </button>
        <button
          onClick={handleDismiss}
          style={{
            background: 'rgba(255,255,255,0.15)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            padding: '12px 20px',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          لاحقاً
        </button>
      </div>
    </div>
  );
}

// مكون أيقونة الجرس مع عداد الإشعارات
export function NotificationBell({ onClick }) {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const loadUnreadCount = async () => {
      const data = await fetchNotifications();
      setUnreadCount(data.unread_count || 0);
    };
    
    loadUnreadCount();
    
    // تحديث العداد كل 30 ثانية
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <button
      onClick={onClick}
      style={{
        position: 'relative',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: '8px'
      }}
    >
      <Bell size={24} color="var(--primary)" />
      {unreadCount > 0 && (
        <span style={{
          position: 'absolute',
          top: '0',
          right: '0',
          background: 'linear-gradient(135deg, #FF5757, #FF3333)',
          color: 'white',
          borderRadius: '50%',
          width: '18px',
          height: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '11px',
          fontWeight: '700',
          boxShadow: '0 2px 6px rgba(255, 87, 87, 0.4)'
        }}>
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
}

// مكون قائمة الإشعارات
export function NotificationList({ onClose }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      const data = await fetchNotifications();
      if (isMounted) {
        setNotifications(data.notifications || []);
        setLoading(false);
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const handleMarkAllRead = async () => {
    await markAllAsRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleNotificationClick = (notification) => {
    playClick();
    if (notification.url) {
      navigate(notification.url);
    }
    if (onClose) onClose();
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message':
        return <MessageCircle size={20} color="#667eea" />;
      case 'connection_request':
        return <Heart size={20} color="#FF6B9D" />;
      case 'connection_accepted':
        return <Check size={20} color="#10B981" />;
      default:
        return <Bell size={20} color="#666" />;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'الآن';
    if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
    if (diffHours < 24) return `منذ ${diffHours} ساعة`;
    if (diffDays < 7) return `منذ ${diffDays} يوم`;
    return date.toLocaleDateString('ar-SA');
  };

  return (
    <div style={{
      position: 'fixed',
      top: '60px',
      left: '20px',
      width: '350px',
      maxHeight: '500px',
      background: 'var(--card-bg, white)',
      borderRadius: '16px',
      boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
      zIndex: 1001,
      overflow: 'hidden',
      animation: 'slideInDown 0.3s ease'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--border-color, #eee)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>
          🔔 الإشعارات
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          {notifications.some(n => !n.read) && (
            <button
              onClick={handleMarkAllRead}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--primary)',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              تحديد الكل كمقروء
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={18} color="#999" />
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div style={{ 
        maxHeight: '400px', 
        overflowY: 'auto',
        overflowX: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }}></div>
            جاري التحميل...
          </div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
            <BellOff size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
            <p>لا توجد إشعارات</p>
          </div>
        ) : (
          notifications.map((notification, index) => (
            <div
              key={notification.notification_id || index}
              onClick={() => handleNotificationClick(notification)}
              style={{
                padding: '14px 20px',
                borderBottom: '1px solid var(--border-color, #f0f0f0)',
                cursor: 'pointer',
                background: notification.read ? 'transparent' : 'rgba(102, 126, 234, 0.05)',
                transition: 'background 0.2s',
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start'
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'var(--light-bg, #f5f5f5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {getNotificationIcon(notification.type)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h4 style={{ 
                  margin: '0 0 4px 0', 
                  fontSize: '14px', 
                  fontWeight: notification.read ? '500' : '700',
                  color: 'var(--text-primary)'
                }}>
                  {notification.title}
                </h4>
                <p style={{ 
                  margin: '0 0 6px 0', 
                  fontSize: '13px', 
                  color: 'var(--text-secondary, #666)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {notification.body}
                </p>
                <span style={{ fontSize: '11px', color: '#999' }}>
                  {formatTime(notification.created_at)}
                </span>
              </div>
              {!notification.read && (
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'var(--primary)',
                  flexShrink: 0,
                  marginTop: '6px'
                }}></div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// مكون إعدادات الإشعارات
export function NotificationSettings() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      setIsSupported(isNotificationSupported());
      const hasSubscription = await hasActiveSubscription();
      setIsEnabled(hasSubscription && isNotificationGranted());
    };
    checkStatus();
  }, []);

  const handleToggle = async () => {
    setLoading(true);
    playClick();
    
    if (isEnabled) {
      await unsubscribeFromNotifications();
      setIsEnabled(false);
    } else {
      const success = await requestNotificationPermission();
      setIsEnabled(success);
      if (success) playSuccess();
    }
    
    setLoading(false);
  };

  if (!isSupported) {
    return (
      <div className="card" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
        <BellOff size={32} style={{ marginBottom: '10px', opacity: 0.5 }} />
        <p>متصفحك لا يدعم الإشعارات الفورية</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '12px',
            background: isEnabled 
              ? 'linear-gradient(135deg, #667eea, #764ba2)' 
              : 'var(--light-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {isEnabled ? (
              <Bell size={22} color="white" />
            ) : (
              <BellOff size={22} color="#999" />
            )}
          </div>
          <div>
            <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600' }}>
              الإشعارات الفورية
            </h4>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
              {isEnabled ? 'مفعّلة - ستصلك إشعارات فورية' : 'معطّلة - لن تصلك إشعارات'}
            </p>
          </div>
        </div>
        
        <button
          onClick={handleToggle}
          disabled={loading}
          style={{
            width: '60px',
            height: '32px',
            borderRadius: '16px',
            border: 'none',
            cursor: 'pointer',
            position: 'relative',
            background: isEnabled 
              ? 'linear-gradient(135deg, #667eea, #764ba2)' 
              : '#ccc',
            transition: 'background 0.3s',
            opacity: loading ? 0.7 : 1
          }}
        >
          <div style={{
            width: '26px',
            height: '26px',
            borderRadius: '50%',
            background: 'white',
            position: 'absolute',
            top: '3px',
            left: isEnabled ? '31px' : '3px',
            transition: 'left 0.3s',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}></div>
        </button>
      </div>
    </div>
  );
}

// تصدير المكون الافتراضي - لوحة إشعارات كاملة
export default function NotificationCenter() {
  const [showList, setShowList] = useState(false);

  return (
    <div style={{ position: 'relative' }}>
      <NotificationBell onClick={() => setShowList(!showList)} />
      {showList && <NotificationList onClose={() => setShowList(false)} />}
    </div>
  );
}
